import React from 'react';
import { OverviewMetrics } from '../../services/analyticsService';
import { Tooltip } from '../ui/Tooltip';

interface Props {
  metrics: OverviewMetrics;
}

const CARD_TOOLTIPS: Record<string, string> = {
  'Précision globale': `Pourcentage de champs correctement extraits par le LLM.

Précision = champs corrects / champs totaux

Basé sur les corrections humaines (extraction_field_corrections).
Un champ non corrigé est considéré correct.

→ 100% = aucune correction humaine nécessaire`,
  'Score qualité': `Score 0-100 basé sur la sévérité des erreurs corrigées.

Score = 100 - (sévérité totale / sévérité max × 100)

Sévérités : format (1), parsing (2), omission (3), hallucination (5)

→ Un score élevé signifie des erreurs mineures`,
  'Documents traités': `Nombre total de documents extraits par le LLM
pour ce domaine, et combien ont été validés par un humain.

Seuls les documents validés contribuent aux métriques
de précision et de qualité.`,
  'Erreurs critiques': `Nombre d'erreurs de sévérité élevée (≥4) détectées
lors des validations humaines.

Inclut les hallucinations (valeurs inventées par le LLM)
et les omissions de champs critiques.

→ 0 = aucune erreur grave détectée`,
};

export const OverviewCards: React.FC<Props> = ({ metrics }) => {
  type ColorType = 'green' | 'yellow' | 'red' | 'blue';

  const cards: Array<{ title: string; value: string; color: ColorType; subtitle?: string }> = [
    {
      title: 'Précision globale',
      value: `${(metrics.global_precision * 100).toFixed(1)}%`,
      color: (metrics.global_precision > 0.9 ? 'green' : metrics.global_precision > 0.7 ? 'yellow' : 'red') as ColorType,
    },
    {
      title: 'Score qualité',
      value: `${metrics.quality_score.toFixed(0)}/100`,
      color: (metrics.quality_score > 85 ? 'green' : metrics.quality_score > 70 ? 'yellow' : 'red') as ColorType,
    },
    {
      title: 'Documents traités',
      value: metrics.total_documents.toString(),
      subtitle: `${metrics.validated_documents} validés`,
      color: 'blue' as ColorType,
    },
    {
      title: 'Erreurs critiques',
      value: (metrics.errors_by_severity?.critical || 0).toString(),
      color: ((metrics.errors_by_severity?.critical || 0) > 0 ? 'red' : 'green') as ColorType,
    },
  ];

  const colorClasses: Record<ColorType, string> = {
    green: 'bg-green-50 border-green-200 text-green-700',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((card, idx) => (
        <div
          key={idx}
          className={`p-4 rounded-lg border ${colorClasses[card.color]}`}
        >
          <div className="flex items-center gap-1">
            <span className="text-sm font-medium opacity-75">{card.title}</span>
            {CARD_TOOLTIPS[card.title] && (
              <Tooltip content={CARD_TOOLTIPS[card.title]} heading={card.title} maxWidth="max-w-md" className="opacity-50" ariaLabel={`Aide ${card.title}`}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </Tooltip>
            )}
          </div>
          <div className="text-2xl font-bold mt-1">{card.value}</div>
          {card.subtitle && (
            <div className="text-xs mt-1 opacity-60">{card.subtitle}</div>
          )}
        </div>
      ))}
    </div>
  );
};
